import { useMutation } from '@apollo/client';
import { useTimeout } from '@churchonline/hooks';
import {
  Badge,
  BadgeType,
  ButtonSize,
  EnvelopeIcon,
  Flex,
  getOpenNetworkTheme,
  IconSize,
  MicrophoneSlashIcon,
  OParagraph,
  OSmallParagraph,
  SecondaryRiskButton,
  Tooltip,
} from '@lifechurch/react-ion';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import React, { MouseEventHandler, useState } from 'react';
import { useTheme } from 'styled-components';
import { AlertType, useAlertContext } from '@lib/alertContext';
import { DELETE_SUBSCRIBER, REINVITE_USER } from '@lib/queries';
import { Avatar, AvatarSizeType } from '@churchonline/core';
import CircleStar from '../CircleStar';
import { FetchState, ReinviteButton } from './ReinviteButton';
import { TableRowWrapper } from './styles';
import {
  DeleteSubscriberMutation,
  ReinviteUserMutation,
  SubscriberNodeFieldsFragment,
} from '@generated/types';
import { useTranslation } from 'next-i18next';

interface Props {
  subscriber: SubscriberNodeFieldsFragment | null | undefined;
}

const mdash = '\u2014';

const TableRow: React.FC<Props> = ({ subscriber }) => {
  const router = useRouter();
  const avatar: string | undefined = subscriber?.node?.avatar ?? undefined;
  const nickname = subscriber?.node?.nickname || mdash;
  const fullName =
    subscriber?.node?.firstName || subscriber?.node?.lastName
      ? `${subscriber?.node?.firstName} ${subscriber?.node?.lastName}`
      : mdash;
  const email = subscriber?.node?.email || mdash;
  const role = subscriber?.node?.role ? subscriber?.node?.role.name : mdash;
  const roleIdentifier = subscriber?.node?.roleIdentifier?.label ?? mdash;
  const roleSymbol = subscriber?.node?.roleIdentifier?.key === 'star' ? <CircleStar /> : undefined;
  const createdAt = subscriber?.node?.createdAt
    ? dayjs(subscriber?.node?.createdAt).format('l')
    : mdash;
  const lastLogin = subscriber?.node?.lastActivity
    ? dayjs(subscriber?.node?.lastActivity).format('l')
    : mdash;
  const { t } = useTranslation(['users', 'common']);

  const onClick = (): void => {
    router.push(`/user/${subscriber?.node?.id}`);
  };

  const theme = useTheme();

  const [inviteState, setInviteState] = useState<FetchState>('idle');

  useTimeout(
    () => {
      setInviteState('idle');
    },
    inviteState === 'idle' ? null : 10000
  );

  const [reinviteUser] = useMutation<ReinviteUserMutation>(REINVITE_USER, {
    onCompleted: data => {
      if (data.reinviteUser?.errors.length === 0) {
        setInviteState('success');
      } else {
        setInviteState('error');
      }
    },
  });

  const handleReinviteUser: MouseEventHandler<HTMLButtonElement> = (e): void => {
    e.preventDefault();
    e.stopPropagation();
    setInviteState('loading');
    reinviteUser({
      variables: {
        input: {
          subscriberId: subscriber?.node?.id || '',
        },
      },
    });
  };

  const { openAlert } = useAlertContext();

  const [deleteSubscriber] = useMutation<DeleteSubscriberMutation>(DELETE_SUBSCRIBER, {
    onCompleted: data => {
      if (data.deleteSubscriber.errors.length === 0) {
        openAlert(
          AlertType.SUCCESS,
          <div>
            {/* TODO: BOLD email */}
            {t('accept_invite_cancel', { email: subscriber?.node?.email })}
          </div>
        );
      }
    },
    update: cache => {
      cache.evict({ id: subscriber?.node?.id, fieldName: 'Subscriber' });
      cache.gc();
    },
  });

  const handleDeleteSubscriber: MouseEventHandler<HTMLButtonElement> = (e): void => {
    e.preventDefault();
    e.stopPropagation();
    deleteSubscriber({
      variables: {
        id: subscriber?.node?.id ?? '',
      },
      refetchQueries: ['Subscribers'],
    });
  };

  return (
    <TableRowWrapper
      data-testid='users-tableRow'
      onClick={onClick}
      isInvite={subscriber?.node?.invitePending ?? false}
    >
      <div>
        {subscriber?.node?.invitePending ? (
          <EnvelopeIcon size={IconSize.LG} />
        ) : (
          <Avatar
            size={AvatarSizeType.XSMALL}
            subscriber={{ avatar, nickname, id: subscriber?.node?.id }}
          />
        )}
      </div>
      {subscriber?.node?.invitePending ? (
        <div>
          <Badge type={BadgeType.INFORMATION}>
            {t('invite_sent')}{' '}
            {dayjs(subscriber?.node?.inviteExpiresAt).subtract(3, 'day').format('L')}
          </Badge>
        </div>
      ) : (
        <OParagraph color='gray100'>{nickname}</OParagraph>
      )}
      <OSmallParagraph>{fullName}</OSmallParagraph>
      <OSmallParagraph color={theme.colors.gray50}>
        {subscriber?.node?.emailConsent ? email : '••••••••••'}
      </OSmallParagraph>
      <OSmallParagraph>{role}</OSmallParagraph>
      <OSmallParagraph>{roleSymbol || roleIdentifier}</OSmallParagraph>
      {subscriber?.node?.invitePending ? (
        <Flex marginInlineStart='auto'>
          <ReinviteButton state={inviteState} onClick={handleReinviteUser} />
          <SecondaryRiskButton
            size={ButtonSize.XS}
            marginInlineStart={3}
            onClick={handleDeleteSubscriber}
          >
            {t('common:cancel')}
          </SecondaryRiskButton>
        </Flex>
      ) : (
        <>
          <OSmallParagraph>{createdAt}</OSmallParagraph>
          <OSmallParagraph>{lastLogin}</OSmallParagraph>
          {subscriber?.node?.muted && (
            <>
              <Tooltip content={t('muted')}>
                <MicrophoneSlashIcon
                  color={getOpenNetworkTheme().colors.error.default}
                  size={IconSize.SM}
                />
              </Tooltip>
            </>
          )}
        </>
      )}
    </TableRowWrapper>
  );
};

export default TableRow;
